import { useContext } from 'react'
import { observer } from 'mobx-react'
import { SelectInput } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { cx } from '@/utils/cx'
import type { ScalarAnswerValue, ScalarQuestion } from '@/types/dataCollection'
import { ChoiceCardGroup } from './ChoiceCardGroup'
import { formatLocalizedNumber, isTileSelect, parseNumberInput, resolveLabel } from './helpers'

interface ScalarQuestionFieldProps {
  question: ScalarQuestion
  value: ScalarAnswerValue | undefined
  error?: string
  onChange: (value: ScalarAnswerValue) => void
  /** Fired when the user leaves a text/number input, so an untouched-but-skipped field can show its error. */
  onBlur?: () => void
  idPrefix?: string
  /**
   * Value the "apply default" button fills in (number questions). Callers that evaluate
   * `defaultFrom` pass the computed suggestion; otherwise the static `defaultValue` is used.
   */
  suggestedValue?: number
}

export const ScalarQuestionField = observer(
  ({
    question,
    value,
    error,
    onChange,
    onBlur,
    idPrefix = '',
    suggestedValue,
  }: ScalarQuestionFieldProps) => {
    const { language } = useContext(StoreContext).TranslationsState
    const id = `${idPrefix}${question.id}`
    const errorId = `${id}-error`
    const invalid = Boolean(error)
    const suggestion =
      suggestedValue ?? (question.type === 'number' ? question.defaultValue : undefined)
    const label = resolveLabel(question.label, language)
    const placeholder = resolveLabel(question.placeholder, language)
    const helpText = resolveLabel(question.helpText, language)
    const unitDisplay = resolveLabel(question.unitLabel, language) || question.unit

    return (
      <div
        className={cx('data-collection__question', invalid && 'data-collection__question--error')}
      >
        {question.type !== 'boolean' && (
          <label className="data-collection__label" htmlFor={id}>
            {label}
          </label>
        )}

        {question.type === 'text' && (
          <input
            id={id}
            className="text-input__input onboarding-text-input"
            type="text"
            placeholder={placeholder}
            maxLength={question.maxLength}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            aria-invalid={invalid || undefined}
            aria-describedby={invalid ? errorId : undefined}
          />
        )}

        {question.type === 'number' && (
          <>
            {unitDisplay ? (
              <NumberWithUnitField
                id={id}
                value={value}
                unit={unitDisplay}
                placeholder={placeholder}
                dataType={question.dataType}
                locale={language}
                min={question.min}
                max={question.max}
                onChange={onChange}
                onBlur={onBlur}
                errorId={invalid ? errorId : undefined}
              />
            ) : (
              <input
                id={id}
                className="text-input__input onboarding-text-input"
                type="number"
                inputMode={question.dataType === 'integer' ? 'numeric' : 'decimal'}
                step={question.dataType === 'integer' ? 1 : undefined}
                placeholder={placeholder}
                min={question.min}
                max={question.max}
                value={value === undefined ? '' : String(value)}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                aria-invalid={invalid || undefined}
                aria-describedby={invalid ? errorId : undefined}
              />
            )}
            {question.applyDefaultLabel && suggestion !== undefined && (
              <button
                type="button"
                className="data-collection__apply-default"
                onClick={() => onChange(suggestion)}
              >
                {resolveLabel(question.applyDefaultLabel, language)}
              </button>
            )}
          </>
        )}

        {question.type === 'select' &&
          (isTileSelect(question) ? (
            <ChoiceCardGroup
              options={question.options}
              value={value as string | undefined}
              onChange={onChange}
              ariaLabel={label}
            />
          ) : (
            <SelectInput
              id={id}
              placeholder={placeholder || undefined}
              options={question.options.map((o) => ({
                value: o.value,
                label: resolveLabel(o.label, language),
              }))}
              value={
                value
                  ? {
                      value: value as string,
                      label: resolveLabel(
                        question.options.find((o) => o.value === value)?.label,
                        language
                      ),
                    }
                  : null
              }
              onChange={(option) => onChange((option as { value: string } | null)?.value ?? '')}
            />
          ))}

        {question.type === 'boolean' && (
          <label className="data-collection__checkbox">
            <input
              id={id}
              type="checkbox"
              checked={value === true}
              onChange={(e) => onChange(e.target.checked)}
              aria-invalid={invalid || undefined}
              aria-describedby={invalid ? errorId : undefined}
            />
            <span>{placeholder || label}</span>
          </label>
        )}

        {helpText && question.type !== 'boolean' && (
          <span className="data-collection__hint">{helpText}</span>
        )}
        {error && (
          <span className="data-collection__error" id={errorId} role="alert">
            {error}
          </span>
        )}
      </div>
    )
  }
)

interface NumberWithUnitFieldProps {
  id: string
  value: ScalarAnswerValue | undefined
  unit: string
  placeholder: string
  dataType?: 'integer' | 'decimal'
  locale: string
  /** Native bounds, applied only to the integer (type="number") variant. */
  min?: number
  max?: number
  onChange: (value: ScalarAnswerValue) => void
  onBlur?: () => void
  /** Id of the visible error message, when the field is currently invalid. */
  errorId?: string
}

const NumberWithUnitField = ({
  id,
  value,
  unit,
  placeholder,
  dataType,
  locale,
  min,
  max,
  onChange,
  onBlur,
  errorId,
}: NumberWithUnitFieldProps) => {
  // Integers use the native number control (spinner, numeric keyboard). Decimals stay a text
  // input so the value can show locale separators, which type="number" does not accept.
  const isInteger = dataType === 'integer'
  const allowDecimal = dataType === 'decimal'
  const raw = value === undefined ? '' : String(value)
  const display = isInteger ? raw : formatLocalizedNumber(raw, locale, allowDecimal)
  const placeholderWithUnit = [placeholder, unit].filter(Boolean).join(' ')

  return (
    <div className="data-collection__number-field">
      <input
        id={id}
        className="text-input__input onboarding-text-input"
        type={isInteger ? 'number' : 'text'}
        inputMode={allowDecimal ? 'decimal' : 'numeric'}
        step={isInteger ? 1 : undefined}
        min={isInteger ? min : undefined}
        max={isInteger ? max : undefined}
        placeholder={placeholderWithUnit}
        value={display}
        onChange={(e) => onChange(parseNumberInput(e.target.value, locale, allowDecimal))}
        onBlur={onBlur}
        aria-invalid={errorId ? true : undefined}
        aria-describedby={errorId}
      />
      {display && (
        <div className="data-collection__number-adornment" aria-hidden="true">
          <span className="data-collection__number-adornment-value">{display}</span>
          <span className="data-collection__number-adornment-unit">{unit}</span>
        </div>
      )}
    </div>
  )
}
